import { differenceInMinutes, parseISO, isAfter, isBefore, addMinutes } from 'date-fns'
import agendamentosService from './agendamentos'

/**
 * Serviço para gerenciar alertas de agendamentos próximos do horário
 */
class AlertasAgendamentosService {
  constructor() {
    this.intervaloVerificacao = null
    this.agendamentosAlertados = new Set() // Para não alertar o mesmo agendamento múltiplas vezes
    this.callbacks = []

    // Configurações padrão
    this.config = {
      minutosAntecedencia: 15, // Alertar 15 minutos antes
      intervaloVerificacaoMs: 60000, // Verificar a cada 1 minuto
      alertarNovamente: false, // Não alertar o mesmo agendamento novamente
    }
  }

  /**
   * Atualiza as configurações do serviço
   * @param {Object} novaConfig
   * @param {number} novaConfig.minutosAntecedencia - Minutos de antecedência para alertar
   * @param {number} novaConfig.intervaloVerificacaoMs - Intervalo de verificação em ms
   * @param {boolean} novaConfig.alertarNovamente - Se deve alertar o mesmo agendamento novamente
   */
  configurar(novaConfig) {
    this.config = { ...this.config, ...novaConfig }
  }

  /**
   * Inicia a verificação periódica de agendamentos próximos
   */
  iniciar() {
    if (this.intervaloVerificacao) {
      return
    }

    // Verifica imediatamente ao iniciar
    this.verificarAgendamentosProximos()

    // E depois continua verificando periodicamente
    this.intervaloVerificacao = setInterval(() => {
      this.verificarAgendamentosProximos()
    }, this.config.intervaloVerificacaoMs)
  }

  /**
   * Para a verificação periódica
   */
  parar() {
    if (this.intervaloVerificacao) {
      clearInterval(this.intervaloVerificacao)
      this.intervaloVerificacao = null
      this.agendamentosAlertados.clear()
    }
  }

  /**
   * Registra um callback para ser chamado quando houver agendamentos próximos
   * @param {Function} callback - Função a ser chamada com (agendamento)
   */
  onAlerta(callback) {
    if (typeof callback === 'function') {
      this.callbacks.push(callback)
    }
  }

  /**
   * Remove um callback registrado
   * @param {Function} callback
   */
  offAlerta(callback) {
    this.callbacks = this.callbacks.filter(cb => cb !== callback)
  }

  /**
   * Verifica se há agendamentos próximos do horário
   */
  async verificarAgendamentosProximos() {
    try {
      const agora = new Date()
      const limiteInferior = agora
      const limiteSuperior = addMinutes(agora, this.config.minutosAntecedencia)

      // Buscar agendamentos do usuário logado com status AGENDADO
      const response = await agendamentosService.buscarMeusAgendamentos({
        per_page: 20, // Buscar um número razoável
      })

      const agendamentos = response.data || []

      // Filtrar agendamentos que estão dentro do intervalo de alerta
      const agendamentosProximos = agendamentos.filter(agendamento => {
        const dataAgendamento = parseISO(agendamento.data_agendamento)

        // Verificar se está dentro do intervalo
        const estaNoIntervalo = isAfter(dataAgendamento, limiteInferior)
          && isBefore(dataAgendamento, limiteSuperior)

        // Verificar se já foi alertado
        const jaAlertado = this.agendamentosAlertados.has(agendamento.id)

        return estaNoIntervalo && (!jaAlertado || this.config.alertarNovamente)
      })

      // Emitir alertas para os agendamentos encontrados
      agendamentosProximos.forEach(agendamento => {
        this.emitirAlerta(agendamento)

        // Marcar como alertado
        if (!this.config.alertarNovamente) {
          this.agendamentosAlertados.add(agendamento.id)
        }
      })

      // Limpar agendamentos antigos do Set (mais de 1 hora atrás)
      this.limparAgendamentosAntigos(agendamentos)
    } catch (error) {
      // Erro silenciosamente
    }
  }

  /**
   * Emite o alerta para todos os callbacks registrados
   * @param {Object} agendamento
   */
  emitirAlerta(agendamento) {
    const dataAgendamento = parseISO(agendamento.data_agendamento)
    const minutosRestantes = differenceInMinutes(dataAgendamento, new Date())

    const alerta = {
      agendamento,
      minutosRestantes,
      dataAgendamento,
    }

    // Chamar todos os callbacks registrados
    this.callbacks.forEach(callback => {
      try {
        callback(alerta)
      } catch (error) {
        // Erro silenciosamente
      }
    })
  }

  /**
   * Remove agendamentos antigos do Set de já alertados
   * @param {Array} agendamentosAtuais
   */
  limparAgendamentosAntigos(agendamentosAtuais) {
    const idsAtuais = new Set(agendamentosAtuais.map(a => a.id))

    // Remover IDs que não existem mais na lista atual
    this.agendamentosAlertados.forEach(id => {
      if (!idsAtuais.has(id)) {
        this.agendamentosAlertados.delete(id)
      }
    })
  }

  /**
   * Reseta o histórico de agendamentos alertados
   */
  resetarHistorico() {
    this.agendamentosAlertados.clear()
  }
}

// Exportar instância singleton
export default new AlertasAgendamentosService()
